import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { ScanLine, FileText, Clock, Users, TrendingUp, ArrowRight, UserPlus } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, accent, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
    className="bg-white border border-[#EBE5D9] rounded-xl p-6"
  >
    <div className="flex items-center justify-between mb-4">
      <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#666]">{label}</span>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}18` }}>
        <Icon className="w-5 h-5" style={{ color: accent }} />
      </div>
    </div>
    <p className="font-serif text-4xl text-[#1A1A1A]">{value}</p>
  </motion.div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [drafts, setDrafts] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, p, d] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/clients", { params: { status: "pending" } }),
        api.get("/invoices", { params: { status: "draft" } }),
      ]);
      setStats(s.data);
      setPending(p.data);
      setDrafts(d.data);
    })();
  }, []);

  return (
    <div className="p-6 md:p-12" data-testid="dashboard">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5E2129] mb-2">Tableau de bord</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A]">Bonjour 👋</h1>
        </div>
        <button
          data-testid="dashboard-scan-cta"
          onClick={() => navigate("/scanner")}
          className="inline-flex items-center gap-3 bg-[#5E2129] hover:bg-[#4A1A20] text-white px-7 py-4 rounded-full font-semibold transition-colors duration-200"
        >
          <ScanLine className="w-5 h-5" />
          Scanner un ticket
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10">
        <button
          data-testid="home-scan-card"
          onClick={() => navigate("/scanner")}
          className="group text-left rounded-2xl p-7 bg-[#5E2129] text-[#FDFBF7] hover:bg-[#4A1A20] transition-colors duration-200"
        >
          <div className="w-12 h-12 rounded-xl bg-[#C28E3A]/25 flex items-center justify-center mb-5">
            <ScanLine className="w-6 h-6 text-[#C28E3A]" />
          </div>
          <h2 className="font-serif text-2xl mb-1">Scanner une facture</h2>
          <p className="text-sm text-[#FDFBF7]/70">Choisir le client puis photographier le ticket de caisse</p>
          <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-[#C28E3A] group-hover:gap-2 transition-all">
            Commencer <ArrowRight className="w-4 h-4" />
          </span>
        </button>

        <button
          data-testid="home-addclient-card"
          onClick={() => navigate("/clients?new=1")}
          className="group text-left rounded-2xl p-7 bg-white border border-[#EBE5D9] hover:border-[#5E2129]/40 transition-colors duration-200"
        >
          <div className="w-12 h-12 rounded-xl bg-[#5E2129]/8 flex items-center justify-center mb-5">
            <UserPlus className="w-6 h-6 text-[#5E2129]" />
          </div>
          <h2 className="font-serif text-2xl text-[#1A1A1A] mb-1">Ajouter un client</h2>
          <p className="text-sm text-[#666]">Créer un nouveau client, puis l'ajouter dans Bexio</p>
          <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-[#5E2129] group-hover:gap-2 transition-all">
            Ajouter <ArrowRight className="w-4 h-4" />
          </span>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <StatCard icon={FileText} label="Factures du jour" value={stats?.today_count ?? "—"} accent="#5E2129" delay={0} />
        <StatCard icon={Clock} label="À valider" value={stats?.drafts ?? "—"} accent="#C28E3A" delay={0.05} />
        <StatCard icon={Users} label="Clients en attente" value={stats?.pending_clients ?? "—"} accent="#D97736" delay={0.1} />
        <StatCard icon={TrendingUp} label="CA encaissé (CHF)" value={stats ? stats.revenue.toFixed(0) : "—"} accent="#2E4F36" delay={0.15} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#EBE5D9] rounded-xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl text-[#1A1A1A]">Factures à valider</h2>
            <button onClick={() => navigate("/factures")} className="text-sm text-[#5E2129] font-semibold flex items-center gap-1 hover:gap-2 transition-all">
              Tout voir <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          {drafts.length === 0 ? (
            <p className="text-[#666] text-sm py-8 text-center">Aucune facture en attente de validation.</p>
          ) : (
            <div className="space-y-2">
              {drafts.slice(0, 5).map((inv) => (
                <button
                  key={inv.id}
                  data-testid={`draft-item-${inv.id}`}
                  onClick={() => navigate(`/factures/${inv.id}`)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg hover:bg-[#FDFBF7] transition-colors duration-200 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[#1A1A1A] truncate">{inv.client_name}</p>
                    <p className="text-xs text-[#666]">{inv.invoice_number}</p>
                  </div>
                  <span className="font-serif text-lg text-[#5E2129] shrink-0 whitespace-nowrap">CHF {inv.total?.toFixed(2)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#EBE5D9] rounded-xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl text-[#1A1A1A]">Nouveaux clients (pending)</h2>
            <button onClick={() => navigate("/clients")} className="text-sm text-[#5E2129] font-semibold flex items-center gap-1 hover:gap-2 transition-all">
              Gérer <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          {pending.length === 0 ? (
            <p className="text-[#666] text-sm py-8 text-center">Aucun nouveau client à créer dans Bexio.</p>
          ) : (
            <div className="space-y-2">
              {pending.slice(0, 5).map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-[#FDFBF7]">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[#1A1A1A] truncate">{c.name}</p>
                    <p className="text-xs text-[#666] truncate">{c.company || "—"}</p>
                  </div>
                  <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#D97736]/10 text-[#D97736] shrink-0 whitespace-nowrap">En attente</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
